import API from "serviceshift-ui/api-client";
import type { ActionContext, Commit, Dispatch } from "vuex";

import type {
  MembershipDashboardFilters,
  MembershipGrowthChartResponse,
  MembershipHealthSummaryResponse,
  MembershipRevenueComparisonResponse
} from "@/MembershipHealthDashboard/types";

interface DashboardSlice {
  membershipDashboardFilters: MembershipDashboardFilters;
  membershipDashboardLoading: boolean;
  membershipDashboardSummary: MembershipHealthSummaryResponse["data"] | null;
  membershipDashboardGrowthChart: MembershipGrowthChartResponse["data"] | null;
  membershipDashboardGrowthChartLoading: boolean;
  membershipDashboardRevenueComparison:
    | MembershipRevenueComparisonResponse["data"]
    | null;
  membershipDashboardRevenueComparisonLoading: boolean;
}

type Ctx = ActionContext<DashboardSlice, unknown> & {
  commit: Commit;
  dispatch: Dispatch;
  state: DashboardSlice;
};

export default {
  fetchMembershipPrograms({ commit }: Ctx, activeOnly = false) {
    return API.membershipProgram
      .getAll(activeOnly)
      .then((res: { data: unknown }) => {
        commit("replace", {
          state: "membershipPrograms",
          data: res.data
        });
      });
  },
  fetchMembershipProgram({ commit }: Ctx, id: number) {
    return API.membershipProgram.get(id).then((res: { data: unknown }) => {
      commit("replace", {
        state: "currentMembershipProgram",
        data: res.data
      });
    });
  },
  clearCurrentMembershipProgram({ commit }: Ctx) {
    commit("replace", {
      state: "currentMembershipProgram",
      data: null
    });
  },
  fetchMembershipDashboardSummary({ commit, state }: Ctx) {
    const ids = state.membershipDashboardFilters.membership_program_ids;
    const params = ids && ids.length ? { membership_program_ids: ids } : {};
    commit("replace", { state: "membershipDashboardLoading", data: true });
    return API.makeRequest("/memberships/health/summary.json", {
      method: "GET",
      params
    })
      .then((res: { data: MembershipHealthSummaryResponse }) => {
        commit("replace", {
          state: "membershipDashboardSummary",
          data: res.data?.data ?? null
        });
      })
      .finally(() => {
        commit("replace", {
          state: "membershipDashboardLoading",
          data: false
        });
      });
  },
  fetchMembershipDashboardGrowthChart({ commit, state }: Ctx) {
    const ids = state.membershipDashboardFilters.membership_program_ids;
    const params = ids && ids.length ? { membership_program_ids: ids } : {};
    commit("replace", {
      state: "membershipDashboardGrowthChartLoading",
      data: true
    });
    return API.makeRequest("/memberships/health/growth_chart.json", {
      method: "GET",
      params
    })
      .then((res: { data: MembershipGrowthChartResponse }) => {
        commit("replace", {
          state: "membershipDashboardGrowthChart",
          data: res.data?.data ?? null
        });
      })
      .finally(() => {
        commit("replace", {
          state: "membershipDashboardGrowthChartLoading",
          data: false
        });
      });
  },
  fetchMembershipDashboardRevenueComparison({ commit, state }: Ctx) {
    const ids = state.membershipDashboardFilters.membership_program_ids;
    const params = ids && ids.length ? { membership_program_ids: ids } : {};
    commit("replace", {
      state: "membershipDashboardRevenueComparisonLoading",
      data: true
    });
    return API.makeRequest(
      "/memberships/health/member_vs_non_member_revenue.json",
      { method: "GET", params }
    )
      .then((res: { data: MembershipRevenueComparisonResponse }) => {
        commit("replace", {
          state: "membershipDashboardRevenueComparison",
          data: res.data?.data ?? null
        });
      })
      .finally(() => {
        commit("replace", {
          state: "membershipDashboardRevenueComparisonLoading",
          data: false
        });
      });
  },
  setMembershipDashboardFilters(
    { commit, dispatch }: Ctx,
    filters: MembershipDashboardFilters
  ) {
    commit("replace", {
      state: "membershipDashboardFilters",
      data: filters
    });
    dispatch("fetchMembershipDashboardGrowthChart");
    dispatch("fetchMembershipDashboardRevenueComparison");
    return dispatch("fetchMembershipDashboardSummary");
  },
  clearMembershipDashboardData({ commit }: Ctx) {
    commit("replace", { state: "membershipDashboardFilters", data: {} });
    commit("replace", { state: "membershipDashboardSummary", data: null });
    commit("replace", { state: "membershipDashboardLoading", data: false });
    commit("replace", { state: "membershipDashboardGrowthChart", data: null });
    commit("replace", {
      state: "membershipDashboardGrowthChartLoading",
      data: false
    });
    commit("replace", {
      state: "membershipDashboardRevenueComparison",
      data: null
    });
    commit("replace", {
      state: "membershipDashboardRevenueComparisonLoading",
      data: false
    });
  }
};
